/* istanbul ignore file */

/**
 * The export file the Content Migration Platform produces, and what M24 answers when
 * it is imported.
 *
 * The file is read in the browser before anything is uploaded, so the operator can see
 * what it contains and which site it is about to land on. Only the fields we actually
 * show are typed here - the file carries more (`export.run_id`, per-item
 * `canonical_data`, ...) and is deliberately read loosely, because the platform owns
 * its shape and a stricter type here would reject files that are perfectly importable.
 *
 * Note what the file does *not* have: a `site_id`. This admin always works in the
 * context of a selected site, so the site is ours to state, not the file's - which is
 * also why the same file can be imported into beta and then into production.
 */

export const CMP_EXPORT_SCHEMA_VERSION = 1;
export const CMP_EXPORT_DESTINATION = 'museum24';

export interface CmpExportMediaEntry {
  dms_id?: string | null;
  /** Set on an envelope-level (library) entry: the asset's own title, caption, alt
   * text and credit. An entry inside an item has no need of it - that item's canonical
   * data describes the placement. */
  metadata?: {
    title?: string | null;
    caption?: string | null;
    alt_text?: string | null;
    credit?: string | null;
  } | null;
  /** Present only on a resolved entry. */
  mimetype?: string | null;
  /** EMS's own split, only ever `image` or `file` - so video and audio are both
   * `file`, which is why the summary classifies on `mimetype` instead. */
  media_kind?: string | null;
  filename?: string | null;
  /** Present only on an *un*resolved entry, naming why it has no EMS id. */
  unresolved_reason?: string | null;
}

export interface CmpExportItem {
  source_object_id?: number;
  media?: Array<CmpExportMediaEntry>;
  canonical_data?: {
    title?: string;
    /** `article`, `landing_page`, ... - the platform owns this list, so it is read as
     * a plain string and shown raw when we have no label for it. */
    kind?: string;
    slug?: string;
    source_url?: string;
    /** BCP 47 tag as the source stated it (`nb-NO`, `en`, `de`), or `x-default` /
     * null when the source did not say - M24 then resolves it server-side from the
     * URL and the source's configured default. */
    language?: { tag?: string | null } | null;
    [key: string]: unknown;
  };
}

export interface CmpExportMetadata {
  source_system?: string;
  /** How many files the source has in EMS - the length of the envelope's `media`
   * list, not the number of references the items make. */
  media_count?: number;
  source_instance?: string;
  ems_target?: string;
  ems_host?: string;
  dms_environment?: string;
  item_count?: number;
  unresolved_media_count?: number;
  created_at?: string;
  platform?: string;
}

export interface CmpExportFile {
  schema_version?: number;
  destination?: string;
  dms_environment?: string;
  items?: Array<CmpExportItem>;
  /** Every file the source has in EMS, whether or not a page in `items` uses it.
   *
   * Separate from the per-item media for a reason: an item's media is the media that
   * item *uses*, while this is everything the website has. Museum24's media gallery
   * lists `resources` rows, and it only ever got rows for media an exported page
   * referenced - so an editor could not pick an image their own museum had already
   * uploaded. Absent from files written before the platform sent it. */
  media?: Array<CmpExportMediaEntry>;
  export?: CmpExportMetadata;
}

/**
 * How the import places pages whose source URL implies a parent (`/forside/barn-og-unge/`).
 *
 * `keep` is what the import has always done: a child is attached to the page that holds
 * the parent's path on this site, or lands at root with the gap recorded when no such
 * page exists. `flat` ignores the source structure entirely and puts every page at root.
 * Sent as the `hierarchy` form field; M24 defaults to `keep` when it is absent.
 */
export type CmpImportHierarchy = 'keep' | 'flat';

export type CmpImportStatus = 'stored' | 'unchanged' | 'skipped' | 'rejected';

export interface CmpImportResultItem {
  source_object_id: number;
  status: CmpImportStatus;
  import_id?: number | null;
  page_id?: number | null;
  error?: string;
  reason?: string;
}

export interface CmpImportSummary {
  stored: number;
  unchanged: number;
  skipped: number;
  rejected: number;
  total: number;
}

/** What M24 answers per media file: `stored` is a row this import created,
 *  `unchanged` one that was already there. */
export interface CmpImportMediaResultItem {
  dms_id: string | null;
  status: CmpImportStatus;
  resource_id?: number | null;
  error?: string;
}

export interface CmpImportResponse {
  results: Array<CmpImportResultItem>;
  summary: CmpImportSummary;
  media_results?: Array<CmpImportMediaResultItem>;
  media_summary?: CmpImportSummary;
}
